import { IconAlertTriangle, IconClick } from "@tabler/icons-react";
import { Text } from "@/components/ui";
import type { EstadoDisenador } from "../../../hooks/use-estado-disenador";
import type { CatalogoNotificaciones } from "../../../services/notificaciones-service";
import { PanelConexion } from "./PanelConexion";
import { PanelEvento } from "./PanelEvento";
import { PanelNotificacion } from "./PanelNotificacion";

interface Props {
  flujoId: string;
  disenador: EstadoDisenador;
  catalogo: CatalogoNotificaciones;
  erroresDelBorrador: string[];
}

/** Columna derecha: propiedades del elemento seleccionado o, sin selección, el estado del borrador. */
export function PanelPropiedades({ flujoId, disenador: d, catalogo, erroresDelBorrador }: Props) {
  const s = d.seleccion;
  const evento = s?.tipo === "evento" ? d.estado.eventos.find((e) => e.clave === s.clave) : undefined;
  const notificacion = s?.tipo === "notificacion" ? d.estado.notificaciones.find((n) => n.clave === s.clave) : undefined;
  const conexion = s?.tipo === "conexion" ? d.estado.conexiones.find((c) => c.clave === s.clave) : undefined;

  return (
    <aside className="min-h-0 overflow-y-auto border-l border-border">
      {evento ? (
        <PanelEvento nodo={evento} estado={d.estado} catalogo={catalogo.eventos} onQuitar={() => d.quitar(evento.clave)} />
      ) : notificacion ? (
        <PanelNotificacion key={notificacion.clave} flujoId={flujoId} notificacion={notificacion} disenador={d} catalogo={catalogo} />
      ) : conexion ? (
        <PanelConexion conexion={conexion} disenador={d} catalogo={catalogo.eventos} />
      ) : (
        <div className="space-y-4 p-4">
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <IconClick size={18} className="mt-0.5 shrink-0" />
            <span>Selecciona un evento, una notificación o una conexión para editar sus propiedades.</span>
          </div>
          {erroresDelBorrador.length > 0 ? (
            <div className="rounded-md border border-advertencia-600/40 p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-advertencia-700 dark:text-advertencia-300">
                <IconAlertTriangle size={14} /> Pendiente para publicar (último guardado)
              </div>
              <ul className="list-disc space-y-0.5 pl-5 text-xs">
                {erroresDelBorrador.map((e) => <li key={e}>{e}</li>)}
              </ul>
            </div>
          ) : (
            <Text size="xs" c="dimmed">El último borrador guardado está listo para publicarse.</Text>
          )}
          {d.sucio ? <Text size="xs" c="dimmed">Hay cambios sin guardar: guarda para revalidar.</Text> : null}
        </div>
      )}
    </aside>
  );
}

/** Encabezado común de los paneles. */
export function EncabezadoPanel({ titulo, subtitulo, acciones }: { titulo: string; subtitulo?: string; acciones?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
      <div className="min-w-0">
        <Text fw={600} size="sm">{titulo}</Text>
        {subtitulo ? <Text size="xs" c="dimmed">{subtitulo}</Text> : null}
      </div>
      {acciones}
    </div>
  );
}
